import type {
  TaskOptionsTree,
  TaskOptionsInitResponse,
  TaskOptionTask,
  TaskOptionCategory,
  TaskOptionType,
} from "@/types/init";

import Fuse from "fuse.js";
import type { MaybeRefOrGetter } from "vue";

export function formatTaskTree(response: TaskOptionsInitResponse): TaskOptionsTree {
  const { categories, tasks, types } = response;
  return categories.map(_category => ({
    ..._category,
    children: types
      .filter(_type => _type.namespace === _category.key)
      .map(_type => ({
        ..._type,
        children: tasks.filter(
          task => task.namespace === `${_category.key}/${_type.key}`
        ),
      })),
  }));
}

export const useTaskConfigDataCore = createSharedComposable(() => {
  const { data, isLoading } = useConfigurationInitialize();
  const { getSrc, fallback } = useTaskIcon();
  const { user } = useCurrentUser();

  const taskItems = computed(() => {
    const locale = (user.value?.locale || "en") as "en";

    return (
      data.value?.tasks
        ?.filter(task => task.enabled && task.visible)
        .map(task => ({
          label:
            task.content?.[locale]?.name || task.content?.[locale]?.title || task.label,
          key: task.key,
          namespace: task.namespace,
          icon: getSrc(task.key, task.icon),
          description: task.content?.[locale]?.description || task.namespace,
          canAddImages: task.canAddImages,
          canAddText: task.canAddText,
          canEditImages: task.canEditImages,
          canAddVideos: task.canAddVideos,
          canRemoveVideos: task.canRemoveVideos,
          canEditText: task.canEditText,
          canRemoveImages: task.canRemoveImages,
          canRemoveText: task.canRemoveText,
          isContentEditable: task.isContentEditable,
          isContentRegenerate: task.isContentRegenerate,
          maxNumberOfImages: task.maxNumberOfImages,
          maxNumberOfVideos: task.maxNumberOfVideos,
        })) || []
    );
  });

  const taskListDb = new Fuse(taskItems.value || [], {
    keys: ["label", "key", "namespace", "description"],
    threshold: 0.2,
  });

  const taskListMap = computed(() => {
    const tasks = data.value?.tasks || [];
    return tasks.reduce(
      (object, task) => {
        const key = `${task.namespace}/${task.key}`;
        object[key] = task;
        return object;
      },
      {} as Record<string, TaskOptionTask>
    );
  });

  const categoryMap = computed(() => {
    console.log("categoryMap computed");
    const categories = data.value?.categories || [];
    return categories.reduce(
      (object, category) => {
        object[category.key] = category;
        return object;
      },
      {} as Record<string, TaskOptionCategory>
    );
  });

  const typeMap = computed(() => {
    const types = data.value?.types || [];
    return types.reduce(
      (object, type) => {
        const key = type.key;
        object[key] = type;
        return object;
      },
      {} as Record<string, TaskOptionType>
    );
  });

  function getTaskByName(name?: string) {
    if (!name) return;
    return taskListMap.value[name];
  }
  function getCategoryByName(name?: string) {
    if (!name) return;
    return categoryMap.value[name];
  }
  function getTypeByName(name?: string) {
    if (!name) return;
    return typeMap.value[name];
  }

  watch(taskItems, current => {
    if (current) {
      taskListDb.setCollection(current);
    }
  });

  return {
    data,
    taskListMap,
    isLoading,
    categoryMap,
    getTaskByName,
    getTypeByName,
    getCategoryByName,
    taskItems,
    taskListDb,
    taskFallbackIcon: fallback,
    getTaskIcon: getSrc,
  };
});

export function newTaskRouter() {
  const route = useRoute();

  const { data, isLoading, getCategoryByName, getTaskByName, getTypeByName } =
    useTaskConfigDataCore();

  const allOptions = computed(() => (data.value ? formatTaskTree(data.value) : []));
  // const category = computed(() => route.params.category as string);
  // const type = computed(() => route.params.type as string);
  // const key = computed(() => route.params.key as string);

  const taskRouteParams = computed(() => ({
    category: route.params.category as string | undefined,
    type: route.params.type as string | undefined,
    key: route.params.key as string | undefined,
  }));

  const taskRouteQuery = computed(() => ({
    category: route.query.category as string | undefined,
    type: route.query.type as string | undefined,
    key: route.query.key as string | undefined,
  }));

  const allTaskOptions = computed(() => data.value?.tasks || []);

  function getCategoryTypeByName(name?: string) {
    if (!name) return;
    const type = getTypeByName(name);
    if (type) return { ...type };
    return;
  }

  const routeLevel = computed(() => {
    if (!taskRouteQuery.value.category) {
      return 0;
    }

    const level1 = allOptions.value.find(o => o.key === taskRouteQuery.value.category);

    if (!level1) {
      return 1;
    }

    if (!taskRouteQuery.value.type) {
      return 1;
    }

    return 2;
  });

  return {
    routeLevel,
    allOptions,
    allTaskOptions,
    isLoading,
    getTaskByName,
    getCategoryByName,
    getCategoryTypeByName,
    initializeTasks: data,
    taskRouteParams,
    taskRouteQuery,
  };
}

export function useSingleTaskConfigDetails(taskKey: MaybeRefOrGetter<string>) {
  const { getTaskByName, getTypeByName, getTaskIcon, taskFallbackIcon } =
    useTaskConfigDataCore();
  const { user } = useCurrentUser();

  const taskConfig = computed(() => {
    const key = toValue(taskKey);
    return getTaskByName(key);
  });

  const taskTypeConfig = computed(() => {
    if (!taskConfig.value) return null;
    const [namespace, category] = taskConfig.value.namespace.split("/");
    return getTypeByName(category || namespace);
  });

  const title = computed(() => {
    const locale = (user.value?.locale || "en") as "en";
    return (
      taskConfig.value?.content?.[locale]?.name ||
      taskConfig.value?.content?.[locale]?.title ||
      taskConfig.value?.label ||
      ""
    );
  });

  const description = computed(() => {
    const locale = (user.value?.locale || "en") as "en";
    return (
      taskConfig.value?.content?.[locale]?.description ||
      taskConfig.value?.namespace?.replace("/", ",") ||
      ""
    );
  });

  const iconUrl = computed(() => {
    return (taskTypeConfig.value || taskConfig.value) &&
      taskTypeConfig.value?.namespace === "social-media"
      ? getTaskIcon(taskTypeConfig.value.key, taskTypeConfig.value.icon)
      : getTaskIcon(taskConfig.value?.key || "", taskConfig.value?.icon || "");
  });

  function onImageLoadError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = taskFallbackIcon;
  }

  return {
    taskConfig,
    taskTypeConfig,
    title,
    description,
    iconUrl,
    onImageLoadError,
  };
}
